import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { contactPhoneNumberQuery } from "../queries";
import { setContactPhoneNumberMutation } from "../commands";

export interface ContactPhoneNumberState {
  readonly phoneNumber: string | null;
  readonly isLoading: boolean;
  readonly isError: boolean;
  /** Replaces the phone number, or clears it when given `null`. Rejects on an invalid (non-international) format. */
  readonly setPhoneNumber: (phoneNumber: string | null) => Promise<void>;
}

/**
 * The calling user's phone number for SMS notification delivery — a single,
 * unscoped value (unlike `useNotificationPreferences`'s per-camera/site
 * cascade), since a phone number belongs to the person, not to any one scope.
 */
export function useContactPhoneNumber(): ContactPhoneNumberState {
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();

  const queryOptions = contactPhoneNumberQuery(apiConfig, {});
  const { data, isLoading, isError } = useQuery(queryOptions);

  const mutationConfig =
    apiConfig.tenant ?
      {
        tenantId: apiConfig.tenant.tenantId,
        apiBaseUrl: apiConfig.tenant.apiBaseUrl,
        authenticatedFetch: apiConfig.authenticatedFetch,
      }
    : { authenticatedFetch: apiConfig.authenticatedFetch };

  const mutation = useMutation(setContactPhoneNumberMutation(mutationConfig, {}));

  const setPhoneNumber = async (phoneNumber: string | null): Promise<void> => {
    const result = await mutation.mutateAsync({ phoneNumber });
    // Cancel any GET for this query still in flight from mount before
    // writing the fresh save result into the cache — otherwise a slower
    // stale response landing afterward would overwrite it, snapping the
    // field back to the pre-save value even though the server already
    // accepted the new one.
    await queryClient.cancelQueries({ queryKey: queryOptions.queryKey });
    queryClient.setQueryData(queryOptions.queryKey, result);
  };

  return {
    phoneNumber: data?.phoneNumber ?? null,
    isLoading,
    isError,
    setPhoneNumber,
  };
}
